import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../Icon.jsx';
import { useAuth } from '../../AuthContext.jsx';
import { supabase } from '../../supabase.js';
import { cloudSetupMessage } from '../../cloudMessage.js';
import { appLinks } from '../navigation.js';
import { checkStaff } from '../../admin/moderation.js';
import { ADMIN_PATH } from '../../admin/adminPath.js';

const accountHelp = [
  ['shield-check', 'Your details stay private', 'Only the tradesperson you choose sees your exact address.'],
  ['rotate-ccw', 'Pick up where you left off', 'Go back to the repair or request you came to manage.'],
  ['store', 'For Businesses', 'Trades and housing providers use the same sign in.'],
];

/** The website's sign-in page. It signs in with the same account service and return rules as the app's sign-in screen. */
export default function SignInPage() {
  const { user, configured } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const nextPath = params.get('next') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // While a sign-in is being submitted, the submit handler picks the destination (staff vs resident), so this redirect waits.
  if (user && !busy) return <Navigate to={nextPath || appLinks.account} replace/>;

  const submit = async (event) => {
    event.preventDefault(); setError(''); setBusy(true);
    try {
      if (!supabase) throw new Error(cloudSetupMessage());
      const response = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (response.error) throw response.error;
      if (!nextPath) {
        // Staff land on their dashboard, not the resident repairs page.
        const access = await checkStaff(supabase);
        if (access.staff) { navigate(ADMIN_PATH, { replace: true }); return; }
      }
      const target = nextPath || (params.get('workspace') === 'supplier' ? '/supplier/opportunities' : '/repairs');
      navigate(target.startsWith('/') ? target : appLinks.account, { replace: true });
    } catch (cause) { setError(cause?.message || cloudSetupMessage()); setBusy(false); }
  };

  return <section className="mk-container mk-signin" aria-labelledby="sign-in-title">
    <div className="mk-signin-card">
      <div className="mk-signin-head">
        <h1 className="mk-display is-44" id="sign-in-title">Sign in</h1>
        <p className="mk-body">See your repairs, quotes and messages.</p>
      </div>
      <form className="mk-signin-form" onSubmit={submit}>
        <label className="mk-field">
          <span className="mk-field-label">Email address</span>
          <input className="mk-input" id="sign-in-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="Your email address" required/>
        </label>
        <div className="mk-field">
          <label className="mk-field-label" htmlFor="sign-in-password">Password</label>
          <div className="mk-input-wrap">
            <input className="mk-input" id="sign-in-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" minLength={8} required/>
            <button type="button" className="mk-show-password" aria-controls="sign-in-password" aria-pressed={showPassword} onClick={() => setShowPassword((shown) => !shown)}>
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={18}/>{showPassword ? 'Hide' : 'Show'}<span className="visually-hidden"> password</span>
            </button>
          </div>
        </div>
        <Link className="mk-text-link" to={appLinks.resetPassword}>Forgot your password?</Link>
        {error && <p className="mk-form-message is-error" role="alert">{error}</p>}
        {!configured && <p className="mk-form-message" role="status">{cloudSetupMessage()}</p>}
        <button className="mk-button is-primary is-h56 is-17 is-block" type="submit" disabled={busy || !configured}>{busy ? 'Please wait…' : 'Sign in'}</button>
      </form>
      <p className="mk-signin-switch">New to Resident Service 360? <Link className="mk-text-link" to={`/sign-up${location.search}`}>Create an account</Link></p>
    </div>
    <aside className="mk-signin-help" aria-labelledby="account-help-title">
      <h2 className="mk-h3 is-28" id="account-help-title">Your Account</h2>
      <ul>{accountHelp.map(([icon, heading, line]) => <li key={heading}>
        <span className="mk-icon-tile is-48 is-white"><Icon name={icon} size={24}/></span>
        <span className="mk-signin-help-text"><span className="mk-signin-help-heading">{heading}</span><span className="mk-signin-help-line">{line}</span></span>
      </li>)}</ul>
    </aside>
  </section>;
}
